import * as React from 'react';

/**
 * [T-FAB] Global FAB context — pages register their "open form" callbacks
 * here so the Floating Action Button in AppLayout can trigger them from any
 * route, without prop-drilling through the entire tree.
 *
 * Usage (in a page):
 *   const { registerFABActions } = useFABContext();
 *   useEffect(() => {
 *     registerFABActions({ onAddTransaction: () => setShowForm(true) });
 *     return () => registerFABActions({});
 *   }, [registerFABActions]);
 */

export interface FABActions {
  onAddTransaction?: () => void;
  onAddAccount?: () => void;
  onAddBudget?: () => void;
  onAddGoal?: () => void;
  onAddSubscription?: () => void;
  onAddBill?: () => void;
}

interface FABContextValue {
  actions: FABActions;
  registerFABActions: (actions: FABActions) => void;
  hideFAB: boolean;
  setHideFAB: (v: boolean) => void;
}

const FABContext = React.createContext<FABContextValue>({
  actions: {},
  registerFABActions: () => {},
  hideFAB: false,
  setHideFAB: () => {},
});

export function FABProvider({ children }: { children: React.ReactNode }) {
  const [actions, setActions] = React.useState<FABActions>({});
  const [hideFAB, setHideFAB] = React.useState(false);

  const registerFABActions = React.useCallback((newActions: FABActions) => {
    setActions(newActions);
  }, []);

  return (
    <FABContext.Provider value={{ actions, registerFABActions, hideFAB, setHideFAB }}>
      {children}
    </FABContext.Provider>
  );
}

export function useFABContext(): FABContextValue {
  return React.useContext(FABContext);
}
